// Portfolio Administration v1.0 PR 4a §7.5 — File UCC-3 Termination modal.

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useFileUccTermination } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type { UccFilingDto } from "@/api/portfolioAdmin/types";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface FileTerminationModalProps {
  loanId: string;
  parent: UccFilingDto;
  open: boolean;
  onClose: () => void;
}

export function FileTerminationModal({
  loanId: _loanId,
  parent,
  open,
  onClose,
}: FileTerminationModalProps) {
  const toast = useToast();
  const fileTermination = useFileUccTermination(_loanId);

  const tz = useUserTimeZone();
  const today = isoDateInTz(new Date(), tz);
  const [filingDate, setFilingDate] = useState(today);
  const [filingNumber, setFilingNumber] = useState("");
  const [authorizationBasis, setAuthorizationBasis] = useState("PayoffComplete");

  const onSubmit = async () => {
    if (!filingNumber.trim()) {
      toast.error("Filing number is required.");
      return;
    }
    if (!authorizationBasis.trim()) {
      toast.error("Authorization basis is required.");
      return;
    }
    try {
      await fileTermination.mutateAsync({
        parentFilingId: parent.id,
        filingDate,
        filingNumber,
        authorizationBasis,
      });
      toast.success("Termination filed.");
      setFilingNumber("");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not file termination.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="File UCC-3 Termination"
      description={`Parent: ${parent.filingNumber}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={() => void onSubmit()}
            disabled={fileTermination.isPending || !filingNumber.trim()}
            data-testid="ucc-termination-submit"
          >
            File termination
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="ucc-termination-form">
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Filing date</span>
          <input
            type="date"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={filingDate}
            onChange={(e) => setFilingDate(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Filing number</span>
          <input
            className="h-9 px-space-2 border border-border-light rounded-md text-small font-mono"
            value={filingNumber}
            onChange={(e) => setFilingNumber(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">
            Authorization basis
          </span>
          <select
            className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
            value={authorizationBasis}
            onChange={(e) => setAuthorizationBasis(e.target.value)}
          >
            <option value="PayoffComplete">Payoff complete</option>
            <option value="DebtorRequest">Debtor request (authorized release)</option>
            <option value="CollateralReleased">Collateral released</option>
            <option value="Other">Other</option>
          </select>
        </label>
      </div>
    </Modal>
  );
}
